import axios from "axios";
import { computed, ComputedRef, reactive } from "vue";

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import ClickStat from "../DTOs/ClickStat";
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from "./types";

export type QuickStats = {
    total: number,
    active_links: number,
    human_hits: number,
    unique_hits: number,
    unique_visitors: number,
};

export type QuickStatsData = {
    dataset: QuickStats
};

export interface iQuickStatsAPI {
    loading: ComputedRef<boolean>,
    setRouter: (router: HttpRouter<"get">) => iQuickStatsAPI,
    fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
    fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
    fetchForTag: (args: FetchForTagArgs) => Promise<void>
    data: ComputedRef<Readonly<ClickStat>>
}

export function DefaultQuickstats(): QuickStats {
    return {
        total: 164,
        active_links: 33,
        unique_hits: 120,
        human_hits: 44,
        unique_visitors: 55,
    };
}

/**
 * The quick stat api provides a list of total clicks for the provided range in the legacy site. It doesnt look like we need this anymore in the new site as we can just get this
 * info directly from the timeline data
 */
export default function QuickStatsAPI(debug = false): iQuickStatsAPI {
    const state = reactive<{
        data: ClickStat,
        loading: boolean
    }>({
        data: new ClickStat("Quickstat API"),
        loading: false,
    });

    let router: HttpRouter<"get"> = axios;

    async function _fetch(url: string): Promise<void> {
        state.loading = true;

        if (debug) {
            const fakeData = DefaultQuickstats();
            state.data = new ClickStat("Quickstat", fakeData.human_hits, fakeData.unique_hits, 0, fakeData.total);
        } else {
            const response: HttpResponse<QuickStatsData> = await router.get(url);

            if (response.status !== 200) {
                throw new Error('Could not fetch quick stats');
            }

            const data = response.data.dataset;
            state.data = new ClickStat(
              "Quickstat",
              data.human_hits,
              data.unique_hits,
              0,
              data.total,
              data.unique_visitors,
              data.active_links
            );
        }

        state.loading = false;
    }

    return {
        loading: computed(() => state.loading),
        data: computed(() => state.data),

        fetchForTeam: async function ({ orgID, teamID = null, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        fetchForAlias: async function ({ orgID, teamID = null, alias, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/summary?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        fetchForTag: async function ({ orgID, teamID = null, tag, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/summary?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        },
    };
}
